/**
 * O rascunho da simulação — a ponte entre o Fee Lab e o Especialista (spec-014).
 *
 * Duas direções, um arquivo, porque é o MESMO objeto visto dos dois lados:
 *
 *  • **Saída** — `montarRascunho()` transcreve o estado do laboratório nos nomes
 *    de parâmetro da tool `analisar_rascunho_simulacao`, com a unidade no nome
 *    de cada campo (ADR-014-A). O consultor não precisa mais guardar o cenário
 *    para perguntar sobre ele.
 *  • **Entrada** — `derivarSugestoes()` transforma o `o_que_mudaria` que a tool
 *    devolveu em ajustes APLICÁVEIS, e `validarSugestao()` recusa o que não cabe
 *    no controle, com a faixa na mensagem.
 *
 * ★ **Nenhum número deste arquivo vem de texto do modelo.** O que sai é o que o
 *   engine calculou; o que entra é `{premissa, direcao}` — sem valor, porque o
 *   backend não recalcula (ADR-014-B) — e o valor proposto sai dos CONTROLES da
 *   tela. É isso que faz o grounding do backend valer também aqui: a tela não
 *   pode exibir número que a IA escreveu, senão o teste de grounding do servidor
 *   vira decoração.
 *
 * Sem `?.`/`??` (Babel @vue/app rejeita). Acentos UTF-8 reais.
 */

import { rPerYear } from '@modules/fee-fixo/utils/fee-lab-format'
import { taxaDoCenario } from '@modules/fee-fixo/constants/plataformas'
import {
  K,
  HORIZONTE_OPTIONS,
  MIGRACAO_OPTIONS,
  FEE_PROF_FAIXA,
} from '@modules/fee-fixo/constants/feeLab'

/**
 * Espelha `SEM_CRUZAMENTO` de `aawz_fee_fixo/core/tools_fee_fixo.py:40`.
 *
 * Existe lá porque *"devolver um ano falso aqui seria pior que devolver nada"*:
 * sem cruzamento no horizonte, o agente afirmaria vantagem que o cenário não
 * mostra. A tela manda a mesma constante em vez de omitir o campo — omitir
 * diria "não sei", e aqui se sabe: não cruza.
 */
export const SEM_CRUZAMENTO = 'nao_cruza_no_horizonte'

const DIRECOES = { aumentar: 1, reduzir: -1 }

const opcoesDe = (options) => options.map((o) => o.value)

/**
 * Os controles da tela que o assistente pode sugerir ajustar.
 *
 * A chave é o nome do parâmetro da tool; `path` é o caminho no estado do
 * laboratório. As faixas vêm de `constants/feeLab` — as MESMAS que o painel
 * usa, para que nunca exista sugestão que o controle recusa.
 */
export const CONTROLES_DO_RASCUNHO = {
  horizonte_anos: {
    path: 'hor',
    rotulo: 'Horizonte',
    unidade: 'anos',
    opcoes: opcoesDe(HORIZONTE_OPTIONS),
  },
  anos_migracao: {
    path: 'mig',
    rotulo: 'Velocidade da migração',
    unidade: 'anos',
    opcoes: opcoesDe(MIGRACAO_OPTIONS),
  },
  fee_profissional_bps: {
    path: 'P.fee_prof',
    rotulo: 'Fee profissional',
    unidade: 'bps',
    min: FEE_PROF_FAIXA.min,
    max: FEE_PROF_FAIXA.max,
    passo: FEE_PROF_FAIXA.step,
  },
}

// ---------------------------------------------------------------------------
// Saída: o estado da tela no vocabulário da tool
// ---------------------------------------------------------------------------

// `null` é ausência, e ausência não é zero: `fee_plataforma_bps: 0` afirmaria
// que a plataforma não cobra nada. O backend trata `None` como desconhecido.
const numeroOuNulo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null
  const n = Number(valor)
  return Number.isFinite(n) ? n : null
}

// Centavo é a menor unidade que o consultor vê; o resto é ruído de ponto
// flutuante que o modelo leria como precisão.
const emReais = (valor) => {
  const n = numeroOuNulo(valor)
  return n === null ? null : Math.round(n * 100) / 100
}

// Só as 7 classes do laboratório, em fração. Chave estranha no objeto viraria
// classe inventada no payload da tool.
const fracoesPorClasse = (alocacao) => {
  if (!alocacao) return null
  const saida = {}
  K.forEach((classe) => {
    saida[classe] = numeroOuNulo(alocacao[classe])
  })
  return saida
}

const valorPorCaminho = (alvo, caminho) => {
  return String(caminho)
    .split('.')
    .reduce((no, chave) => (no ? no[chave] : undefined), alvo)
}

/**
 * O rascunho da tela nos parâmetros tipados da tool.
 *
 * @param {Object=} opts
 * @param {Object=} opts.state      Estado do laboratório (cenário ativo).
 * @param {Object=} opts.resultado  Saída do `simulate(state)`.
 * @returns {Object|null} Payload com os 11 campos, ou null sem simulação.
 */
export const montarRascunho = ({ state, resultado } = {}) => {
  if (!state || !resultado) return null
  const P = state.P || {}
  const save = resultado.save || {}
  const advGain = resultado.advGain || {}
  const ganhoBps = numeroOuNulo(advGain.fee)

  return {
    horizonte_anos: numeroOuNulo(state.hor),
    anos_migracao: numeroOuNulo(state.mig),
    fee_profissional_bps: numeroOuNulo(P.fee_prof),
    // ★ 13/08/2026 — a taxa RESOLVIDA, a mesma que o motor publica. Ler
    // `P.fee_plat` cru fazia o agente receber 25 (a premissa do escritório)
    // num cenário BTG que a tela mostrava em 30: ele citaria ao consultor um
    // número que a tela não exibe — indistinguível de invenção, do lado dele.
    fee_plataforma_bps: numeroOuNulo(taxaDoCenario(state, P, state.auc)),
    alocacao_fee: fracoesPorClasse(state.alloc),
    alocacao_comissionado: fracoesPorClasse(P.alc_c),
    rentabilidade_aa: numeroOuNulo(state.rent),
    alfa_aa: numeroOuNulo(state.alfa),
    economia_cliente_reais: emReais(save.cliente),
    // A MESMA conta do KPI da tela (`fee-lab-kpi-strip`): o agente cita o
    // número que o consultor está vendo, não uma segunda conversão.
    ganho_assessor_ano_reais:
      ganhoBps === null ? null : emReais(rPerYear(ganhoBps, state.auc)),
    cruzamento: resultado.cross ? resultado.cross : SEM_CRUZAMENTO,
  }
}

const comoTexto = (valor) => {
  if (valor === null || valor === undefined) return 'desconhecido'
  if (typeof valor === 'object') return JSON.stringify(valor)
  return String(valor)
}

/**
 * O bloco que acompanha a pergunta — canal SEPARADO do contexto genérico.
 *
 * `montarContexto` descreve onde o consultor está e quem está em foco, e segue
 * sem dígito nenhum (ADR-014-A). Os números viajam aqui, já com o nome do
 * parâmetro da tool, para o modelo TRANSCREVER em vez de parafrasear: parafrase
 * é onde a unidade se perde e 70 bps vira 70 reais.
 *
 * @param {Object|null} rascunho Saída de `montarRascunho`.
 * @returns {string} Bloco de texto, ou '' quando não há rascunho.
 */
// A abertura do bloco. É por ela que a RENDERIZAÇÃO do histórico o reconhece
// para não mostrá-lo na bolha do consultor (`falaDoConsultor`, em
// `api/apiFeeFixoIa.js`): quem monta e quem desfaz leem a mesma constante, senão
// o dia em que este texto mudar o strip para de casar em silêncio.
export const MARCA_DO_RASCUNHO = '[Rascunho da simulação na tela'

export const textoDoRascunho = (rascunho) => {
  if (!rascunho) return ''
  const linhas = Object.keys(rascunho).map(
    (campo) => `${campo}: ${comoTexto(rascunho[campo])}`
  )
  return [
    MARCA_DO_RASCUNHO + ' — ainda NÃO salvo. Passe estes valores,',
    'sem alterá-los, para a tool analisar_rascunho_simulacao:',
    linhas.join('\n'),
    ']',
  ].join('\n')
}

// ---------------------------------------------------------------------------
// Entrada: o que a tool sugeriu, virando ajuste aplicável
// ---------------------------------------------------------------------------

const listaDeOpcoes = (opcoes) => {
  if (opcoes.length === 1) return String(opcoes[0])
  return opcoes.slice(0, -1).join(', ') + ' ou ' + opcoes[opcoes.length - 1]
}

const faixaEmTexto = (controle) => {
  if (controle.opcoes) {
    return `aceita ${listaDeOpcoes(controle.opcoes)} ${controle.unidade}`
  }
  return (
    `aceita de ${controle.min} a ${controle.max} ${controle.unidade}, ` +
    `de ${controle.passo} em ${controle.passo}`
  )
}

// Próximo valor do CONTROLE na direção pedida, ou null quando já está no
// extremo. O valor nasce aqui — nunca do que o modelo escreveu.
const proximoValor = (controle, atual, sentido) => {
  if (controle.opcoes) {
    const ordenadas = [...controle.opcoes].sort((a, b) => a - b)
    const candidatas =
      sentido > 0
        ? ordenadas.filter((v) => v > atual)
        : ordenadas.filter((v) => v < atual).reverse()
    return candidatas.length ? candidatas[0] : null
  }
  const proximo = atual + sentido * controle.passo
  if (proximo < controle.min || proximo > controle.max) return null
  return proximo
}

/**
 * Valida um ajuste ANTES de ele tocar o estado da tela.
 *
 * Fronteira única: quem aplica passa por aqui, venha a sugestão de onde vier —
 * do payload da tool hoje, de um campo editável amanhã. Confiar em quem chama
 * criaria uma porta que aplica sem validar, que é como um `horizonte: 10` (o
 * controle só aceita 1, 3 ou 5) entraria no cenário que vai virar PDF.
 *
 * @param {Object=} opts
 * @param {string=} opts.premissa Nome do parâmetro da tool.
 * @param {number=} opts.valor    Valor pretendido.
 * @returns {{valido: boolean, motivo: string}}
 */
export const validarSugestao = ({ premissa, valor } = {}) => {
  const controle = CONTROLES_DO_RASCUNHO[premissa]
  if (!controle) {
    return {
      valido: false,
      motivo:
        `O assistente sugeriu ajustar "${premissa}", que não é um controle ` +
        'da simulação. Ajuste manualmente no painel de premissas.',
    }
  }
  const n = Number(valor)
  if (valor === null || valor === undefined || valor === '' || !Number.isFinite(n)) {
    return {
      valido: false,
      motivo: `Valor inválido para ${controle.rotulo}. Ajuste manualmente no painel.`,
    }
  }
  if (controle.opcoes && controle.opcoes.indexOf(n) === -1) {
    return {
      valido: false,
      motivo: `${controle.rotulo} ${faixaEmTexto(controle)} — ${n} está fora.`,
    }
  }
  if (!controle.opcoes) {
    const foraDaFaixa = n < controle.min || n > controle.max
    const foraDoPasso = (n - controle.min) % controle.passo !== 0
    if (foraDaFaixa || foraDoPasso) {
      return {
        valido: false,
        motivo: `${controle.rotulo} ${faixaEmTexto(controle)} — ${n} está fora.`,
      }
    }
  }
  return { valido: true, motivo: '' }
}

/**
 * Os ajustes que o consultor pode aplicar com um clique.
 *
 * Lê `{premissa, direcao}` do payload e IGNORA qualquer outro campo do item —
 * inclusive um `valor_sugerido` que apareça ali. O valor é sempre o próximo
 * degrau do controle, calculado sobre o estado atual da tela.
 *
 * @param {Object=} opts
 * @param {Array=}  opts.oQueMudaria Itens `o_que_mudaria` da tool.
 * @param {Object=} opts.state       Estado do laboratório.
 * @returns {Array<Object>} Sugestões normalizadas (lista vazia quando não há).
 */
export const derivarSugestoes = ({ oQueMudaria, state } = {}) => {
  if (!Array.isArray(oQueMudaria) || !state) return []

  const sugestoes = []
  oQueMudaria.forEach((item) => {
    if (!item) return
    const controle = CONTROLES_DO_RASCUNHO[item.premissa]
    const sentido = DIRECOES[item.direcao]
    if (!controle || !sentido) return

    const valorAtual = numeroOuNulo(valorPorCaminho(state, controle.path))
    if (valorAtual === null) return

    const valorSugerido = proximoValor(controle, valorAtual, sentido)
    const aplicavel = valorSugerido !== null

    sugestoes.push({
      premissa: item.premissa,
      direcao: item.direcao,
      // Texto do backend, sem número por construção (`O_QUE_MUDARIA`).
      efeito: String(item.efeito || ''),
      rotulo: controle.rotulo,
      unidade: controle.unidade,
      path: controle.path,
      valorAtual,
      valorSugerido,
      aplicavel,
      texto: aplicavel
        ? `${controle.rotulo}: ${valorAtual} → ${valorSugerido} ${controle.unidade}`
        : `${controle.rotulo}: ${valorAtual} ${controle.unidade}`,
      motivo: aplicavel
        ? ''
        : `${controle.rotulo} ${faixaEmTexto(controle)} e já está no limite ` +
          `desse lado (${valorAtual}).`,
    })
  })
  return sugestoes
}
